import { Injectable, OnDestroy } from '@angular/core';
import { Router } from '@angular/router';
import Keycloak from 'keycloak-js';
import { BehaviorSubject, interval, Observable, Subscription } from 'rxjs';
import { environment } from '../../../assets/environments/environment';
import { HttpClient } from '@angular/common/http';
import { UrlReferenceService } from './url-reference.service';
import { CompanyService } from './company.service';
import { LoggerService } from './logger.service';

@Injectable({
  providedIn: 'root',
})
export class AuthService implements OnDestroy {
  private keycloak: Keycloak;
  private isAuthenticatedSubject = new BehaviorSubject<boolean>(false);
  public isAuthenticated$ = this.isAuthenticatedSubject.asObservable();
  private userData: any;
  private userProfileSubject =
    new BehaviorSubject<Keycloak.KeycloakProfile | null>(null);
  public userProfile$ = this.userProfileSubject.asObservable();
  private tokenCheckInterval = 60000;
  private tokenCheckSubscription?: Subscription;
  public isInitialized$ = new BehaviorSubject<boolean>(false);
  private profileLoadingSubject = new BehaviorSubject<boolean>(false);
  public profileLoading$ = this.profileLoadingSubject.asObservable();
  // Added refreshToken to storage keys
  private readonly storageKeys = [
    'keycloakToken',
    'refreshToken',
    'company_response_data',
    'crNumber',
    'companyId',
    'keycloakUserProfile',
    'role',
    'token',
    'sponsorId',
    'sponsorName',
    'from-signup',
    'from-get-quote',
    'from-dashboard',
    'savedPolicyStartDate',
    'company',
    'companyData',
    'selectedCompany',
    // Note: 'language' is excluded here to preserve user's language preference
  ];

  get keycloakInstance(): Keycloak {
    return this.keycloak;
  }

  constructor(
    private router: Router,
    private http: HttpClient,
    private urlRef: UrlReferenceService,
    private companyService: CompanyService,
    private logger: LoggerService
  ) {
    this.keycloak = new Keycloak({
      url: environment.backend.keycloak.url,
      realm: environment.backend.keycloak.realm,
      clientId: environment.backend.keycloak.clientId,
    });
    this.initTokenExpiryMonitor();
  }

  ngOnDestroy(): void {
    this.stopTokenExpiryMonitor();
  }

  private initTokenExpiryMonitor(): void {
    this.logger.log('Initializing token expiry monitor...');
    this.tokenCheckSubscription = interval(this.tokenCheckInterval).subscribe(
      () => {
        this.checkTokenExpiry();
      }
    );
  }

  private stopTokenExpiryMonitor(): void {
    if (this.tokenCheckSubscription) {
      this.tokenCheckSubscription.unsubscribe();
    }
  }

  private checkTokenExpiry(): void {
    if (!this.keycloak.authenticated) {
      this.logger.log('⏰ Token expiry check: Not authenticated, skipping');
      return;
    }

    const now = Math.floor(Date.now() / 1000);
    const tokenExpiry = this.getTokenExpiry();
    const expiresIn = tokenExpiry - now;

    this.logger.log('⏰ Token expiry check:', {
      now,
      tokenExpiry,
      expiresIn,
      tokenExists: !!this.keycloak.token,
      authenticated: this.keycloak.authenticated,
      tokenIssuedAt: (this.keycloak.tokenParsed as any)?.iat,
      tokenNotBefore: (this.keycloak.tokenParsed as any)?.nbf,
    });

    if (expiresIn < 0) {
      // Token is expired
      this.logger.log('❌ Token expired (expiresIn=' + expiresIn + '), logging out');
      this.logout();
      this.router.navigate(['/'], {
        queryParams: { sessionExpired: true },
      });
    } else if (expiresIn < 300) {
      // Token expires in less than 5 minutes
      this.logger.log('⚠️ Token expiring soon (in ' + expiresIn + 's), refreshing');
      this.refreshCurrentToken().catch((error) => {
        console.error('Failed to refresh token during expiry check:', error);
        this.logout();
        this.router.navigate(['/'], {
          queryParams: { sessionExpired: true },
        });
      });
    } else {
      this.logger.log('✅ Token is valid, expires in', expiresIn, 'seconds');
    }
  }

  async init(): Promise<boolean> {
    this.cleanupOldKeycloakCallbacks();

    try {
      // Check if we have a valid token in storage first
      const storedToken = localStorage.getItem('keycloakToken');
      const storedRefreshToken = localStorage.getItem('refreshToken');

      if (storedToken && storedRefreshToken) {
        // Try to use stored tokens for a smoother experience
        try {
          this.keycloak.token = storedToken;
          this.keycloak.refreshToken = storedRefreshToken;
          this.logger.log('Using stored tokens for initialization');
        } catch (e) {
          this.logger.warn('Could not restore tokens from storage:', e);
        }
      }

      // Increase timeout to be more forgiving
      const initializationTimeout = 10000; // 10 seconds should be enough
      let timeoutHandle: ReturnType<typeof setTimeout>;

      const timeoutPromise = new Promise<never>((_, reject) => {
        timeoutHandle = setTimeout(() => {
          reject(
            new Error(
              `Keycloak initialization timed out after ${initializationTimeout}ms`
            )
          );
        }, initializationTimeout);
      });

      // Standard initialization for all environments
      const tryKeycloakInit = async (): Promise<boolean> => {
        return await this.keycloak.init({
          onLoad: 'check-sso',
          silentCheckSsoRedirectUri: window.location.origin + '/assets/silent-check-sso.html',
          checkLoginIframe: false,
          enableLogging: true,
          responseMode: 'fragment',
          flow: 'standard',
          pkceMethod: 'S256',
          silentCheckSsoFallback: false,
          redirectUri: environment.backend.keycloak.keycloakRedirectUri,
        });
      };

      const initPromise = tryKeycloakInit()
        .then((authenticated) => {
          clearTimeout(timeoutHandle);
          this.logger.log(
            'Keycloak initialization completed. Authenticated:',
            authenticated
          );
          return authenticated;
        })
        .catch((error) => {
          clearTimeout(timeoutHandle);
          this.logger.error('Keycloak init promise failed:', error);
          // Don't throw the error, return false instead to allow graceful degradation
          return false;
        });

      const authenticated: boolean = await Promise.race([
        initPromise,
        timeoutPromise,
      ]).catch(async (error): Promise<boolean> => {
        this.logger.error('Keycloak initialization error:', error);
        clearTimeout(timeoutHandle);
        return false;
      });

      console.log('🔍 [AuthService] Init result:', {
        authenticated,
        keycloakAuthenticated: this.keycloak.authenticated,
        keycloakToken: !!this.keycloak.token,
        keycloakEmail: (this.keycloak.tokenParsed as any)?.email,
        hasCallbackCode: new URLSearchParams(window.location.search).has('code')
      });

      this.logger.log('🔓 Setting isAuthenticatedSubject to:', authenticated);
      this.isAuthenticatedSubject.next(authenticated);

      if (authenticated) {
        await this.handleAuthenticatedState();
      } else {
        // Instead of silent failure, log the state clearly
        this.logger.log('User is not authenticated after Keycloak init');
        // Clear any potentially stale tokens
        localStorage.removeItem('keycloakToken');
        localStorage.removeItem('refreshToken');
      }

      this.isInitialized$.next(true);
      return authenticated;
    } catch (error) {
      console.error('[AuthService] Critical initialization error:', error);
      this.isAuthenticatedSubject.next(false);
      this.isInitialized$.next(true);
      this.handleInitFailure();
      return false;
    }
  }

  private async handleAuthenticatedState(): Promise<void> {
    try {
      this.cleanupOldKeycloakCallbacks();

      // More aggressive token refresh
      const refreshed = await this.keycloak.updateToken(60);
      if (refreshed) {
        this.logger.log('Token refreshed during initialization');
      }

      if (!this.keycloak.token) {
        throw new Error('No token available after refresh');
      }

      localStorage.setItem('keycloakToken', this.keycloak.token);
      if (this.keycloak.refreshToken) {
        localStorage.setItem('refreshToken', this.keycloak.refreshToken);
      }

      // Load user profile but don't block authentication on failure
      // The user is already authenticated at this point
      this.loadUserProfileWithRetry().catch((error) => {
        this.logger.warn('Profile loading failed (non-blocking):', error);
        // Don't logout user if profile load fails - user is still authenticated
      });
      
      // Don't check token expiry immediately - the token was just obtained
      // The periodic check (every 60 seconds) will handle it
      // Checking immediately after auth can cause false positives
      this.logger.log('✅ Authentication successful, token expiry monitor will check periodically');
    } catch (error) {
      console.error('Error during authenticated initialization:', error);
      // Only logout if token refresh/validation fails, not for other errors
      if (error instanceof Error && error.message.includes('token')) {
        this.isAuthenticatedSubject.next(false);
        localStorage.removeItem('keycloakToken');
        localStorage.removeItem('refreshToken');
      }
    }
  }

  public async loadUserProfileWithRetry(retries = 3): Promise<void> {
    this.profileLoadingSubject.next(true);
    try {
      this.logger.log(`Loading user profile (attempt with ${retries} retries available)...`);
      const profile = await this.loadUserProfile();
      this.userProfileSubject.next(profile);
      localStorage.setItem('keycloakUserProfile', JSON.stringify(profile));
      this.logger.log('✅ User profile saved to storage');
    } catch (error) {
      if (retries > 0) {
        this.logger.log(`Profile load failed, retrying in 2 seconds (${retries} attempts remaining)...`);
        await new Promise((resolve) => setTimeout(resolve, 2000));
        return this.loadUserProfileWithRetry(retries - 1);
      }
      console.error('❌ Failed to load user profile after all retries:', error);
      // Don't throw - user is still authenticated even without profile
    } finally {
      this.profileLoadingSubject.next(false);
    }
  }

  async loadUserProfile(): Promise<Keycloak.KeycloakProfile> {
    if (!this.keycloak.authenticated) {
      throw new Error('Not authenticated');
    }

    try {
      // Refresh token before profile load to ensure it's valid
      const refreshed = await this.keycloak.updateToken(5);
      this.logger.log('Token refresh before profile load:', {
        refreshed,
        hasToken: !!this.keycloak.token,
        tokenParsed: this.keycloak.tokenParsed
      });

      if (!this.keycloak.token) {
        throw new Error('No token available for profile loading');
      }

      this.logger.log('Attempting to load user profile...');
      
      // Try to use the keycloak library's built-in method
      // It should call /userinfo or /account endpoint
      try {
        const profile = await this.keycloak.loadUserProfile();
        this.logger.log('✅ User profile loaded successfully:', profile);
        return profile;
      } catch (loadError) {
        this.logger.warn('keycloak.loadUserProfile() failed, trying fallback...', loadError);
        
        // Fallback: Extract profile info from the token itself
        // The token contains user information that we can use
        if (this.keycloak.tokenParsed) {
          const tokenProfile: Keycloak.KeycloakProfile = {
            id: (this.keycloak.tokenParsed as any).sub,
            username: (this.keycloak.tokenParsed as any).preferred_username,
            email: (this.keycloak.tokenParsed as any).email,
            firstName: (this.keycloak.tokenParsed as any).given_name,
            lastName: (this.keycloak.tokenParsed as any).family_name,
            emailVerified: (this.keycloak.tokenParsed as any).email_verified,
          };
          this.logger.log('✅ Using fallback profile from token:', tokenProfile);
          return tokenProfile;
        }
        
        throw loadError;
      }
    } catch (error) {
      console.error('Error loading user profile:', {
        error,
        errorMessage: error instanceof Error ? error.message : String(error),
        authenticated: this.keycloak.authenticated,
        hasToken: !!this.keycloak.token,
      });
      throw error;
    }
  }

  getCachedUserProfile(): Keycloak.KeycloakProfile | null {
    try {
      const profile = localStorage.getItem('keycloakUserProfile');
      return profile ? JSON.parse(profile) : null;
    } catch (error) {
      this.logger.error('Error parsing cached profile:', error);
      return null;
    }
  }

  setupSilentCheckSsoCallback(): void {
    window.addEventListener('message', (event) => {
      if (event.origin !== window.location.origin) {
        return;
      }

      // Handle both URL data and 'check-sso' message
      if (
        event.data === 'check-sso' ||
        (typeof event.data === 'string' &&
          event.data.includes('silent-check-sso'))
      ) {
        this.logger.log('Silent SSO callback received:', event.data);

        this.keycloak
          .updateToken(30)
          .then((refreshed) => {
            if (refreshed) {
              this.logger.log('Token refreshed during silent SSO check');
              localStorage.setItem('keycloakToken', this.keycloak.token!);
              if (this.keycloak.refreshToken) {
                localStorage.setItem(
                  'refreshToken',
                  this.keycloak.refreshToken
                );
              }
              this.isAuthenticatedSubject.next(true);
            }
          })
          .catch((error) => {
            console.error('Silent SSO token refresh failed:', error);
            this.isAuthenticatedSubject.next(false);
          });
      }
    });
  }

  login(redirectPath?: string): void {
    // Clear any potentially stale tokens before login
    localStorage.removeItem('keycloakToken');
    localStorage.removeItem('refreshToken');

    // Determine redirect path for post-login navigation (not for Keycloak redirect URI)
    let targetPath = redirectPath;
    if (!targetPath) {
      // Check for returnUrl in query parameters
      const urlParams = new URLSearchParams(window.location.search);
      const returnUrl = urlParams.get('returnUrl');
      targetPath = returnUrl || '/dashboard/home';
    }

    // Store the intended redirect path for app.component to use after auth completes
    sessionStorage.setItem('loginRedirectPath', targetPath);

    this.logger.log('🔐 Initiating Keycloak login with post-auth redirect to:', targetPath);
    
    // Call Keycloak login WITHOUT specifying redirectUri
    // This lets Keycloak use the default configured redirect URI instead
    this.keycloak.login();
  }

  logout(): void {
    try {
      console.log('🔴 LOGOUT called - clearing authentication state');
      console.trace('Logout stack trace (logout was called from):');

      // Update authentication state first
      this.isAuthenticatedSubject.next(false);
      this.userProfileSubject.next(null);

      // Clear all application storage
      this.clearAllAppStorage();

      this.logger.log(
        'Application storage cleared, redirecting to Keycloak logout...'
      );

      // Perform Keycloak logout with redirect
      const logoutOptions = {
        redirectUri: encodeURI(window.location.origin),
      };
      this.keycloak.logout(logoutOptions);
    } catch (error) {
      console.error('Logout error:', error);
      // Fallback cleanup
      this.clearAllAppStorage();
      this.keycloak.clearToken();

      // Force redirect to home page
      window.location.href = window.location.origin;
    }
  }

  private handleInitFailure(): void {
    this.isAuthenticatedSubject.next(false);
    this.isInitialized$.next(true);

    // Don't reload on refresh - this causes 502 errors
    // Instead, just redirect to login without reload
    const currentUrl = window.location.pathname;

    // Only redirect if not already on login/landing page
    if (currentUrl !== '/' && !currentUrl.includes('/landing')) {
      this.logger.log('Auth initialization failed, redirecting to login');
      this.router.navigate(['/'], {
        queryParams: { authFailed: true },
        replaceUrl: true, // Replace current URL in history
      });
    }
  }

  private clearAllAppStorage(): void {
    this.cleanupOldKeycloakCallbacks();
    this.companyService.clearSelectedCompany();

    // Clear app-specific storage but preserve language preference
    this.storageKeys.forEach((key) => {
      if (key !== 'language') {
        // Keep language preference
        localStorage.removeItem(key);
      }
    });

    this.storageKeys.forEach((key) => {
      if (key !== 'language') {
        // Keep language preference
        sessionStorage.removeItem(key);
      }
    });

    // Clear any additional localStorage keys that might not be in the storageKeys array
    const additionalKeys = [
      'loginRedirectPath',
      'keycloakUserProfile',
      'company_response_data',
    ];

    additionalKeys.forEach((key) => {
      localStorage.removeItem(key);
      sessionStorage.removeItem(key);
    });

    // Clear user data
    this.userData = null;

    console.log('All application storage cleared on logout');
  }

  getToken(): Promise<string> {
    if (!this.keycloak.authenticated) {
      return Promise.reject(new Error('Not authenticated'));
    }

    return this.keycloak
      .updateToken(30)
      .then(() => {
        if (!this.keycloak.token) {
          throw new Error('No token available after update');
        }

        const token = this.keycloak.token;
        localStorage.setItem('keycloakToken', token);
        if (this.keycloak.refreshToken) {
          localStorage.setItem('refreshToken', this.keycloak.refreshToken);
        }

        return token;
      })
      .catch((error) => {
        console.error('Failed to get token:', error);
        // Only redirect to login if it's an authentication error
        if (
          error.message.includes('authentication') ||
          error.message.includes('Not authenticated')
        ) {
          this.login();
        }
        throw error;
      });
  }

  async forceTokenRefresh(): Promise<void> {
    if (!this.keycloak.authenticated) {
      throw new Error('Not authenticated');
    }

    try {
      await this.keycloak.updateToken(-1);
      if (!this.keycloak.token) {
        throw new Error('No token available after force refresh');
      }

      localStorage.setItem('keycloakToken', this.keycloak.token);
      if (this.keycloak.refreshToken) {
        localStorage.setItem('refreshToken', this.keycloak.refreshToken);
      }

      await this.loadUserProfile();
    } catch (error) {
      console.error('Force token refresh failed:', error);
      // Update authentication state
      this.isAuthenticatedSubject.next(false);
      throw error;
    }
  }

  refreshCurrentToken(): Promise<void> {
    // Don't attempt refresh if not authenticated
    if (!this.keycloak.authenticated) {
      return Promise.reject(new Error('Not authenticated'));
    }

    return this.keycloak
      .updateToken(30)
      .then((refreshed) => {
        if (refreshed) {
          console.log('Token refreshed successfully');
          if (!this.keycloak.token) {
            throw new Error('No token available after refresh');
          }

          localStorage.setItem('keycloakToken', this.keycloak.token);
          if (this.keycloak.refreshToken) {
            localStorage.setItem('refreshToken', this.keycloak.refreshToken);
          }
        }
        return;
      })
      .catch((error) => {
        console.error('Failed to refresh token:', error);
        // Mark as unauthenticated to prevent further API calls
        this.isAuthenticatedSubject.next(false);
        // Clear any potentially corrupted tokens
        localStorage.removeItem('keycloakToken');
        localStorage.removeItem('refreshToken');
        throw error;
      });
  }

  async getUserInfo(): Promise<Keycloak.KeycloakProfile> {
    if (!this.keycloak.authenticated || !this.keycloak.token) {
      throw new Error('Not authenticated or no token available');
    }

    if (!this.keycloak.tokenParsed) {
      throw new Error('No token available');
    }

    try {
      return this.keycloak.loadUserProfile();
    } catch (error) {
      console.error('Error loading user profile:', error);
      throw error;
    }
  }

  getRoles(): string[] {
    return this.keycloak.realmAccess?.roles || [];
  }

  hasRole(role: string): boolean {
    return this.getRoles().includes(role);
  }

  signup(signupData: any): Observable<any> {
    return this.http.post(this.urlRef.getSignupUrl(), signupData);
  }

  generateOtp(payload: {
    otpSentVia: string;
    email?: string;
    phoneNumber: string;
    otpType?: string | null;
    language?: string | null;
  }): Observable<any> {
    return this.http.post(this.urlRef.getOtpUrl(), payload, {
      responseType: 'text',
    });
  }

  reGenerateOtp(payload: {
    otpSentVia: string;
    email?: string;
    phoneNumber: string;
    otpType?: string | null;
    language?: string | null;
  }): Observable<any> {
    return this.http.post(this.urlRef.getRegenerateOtpUrl(), payload, {
      responseType: 'text',
    });
  }

  validateOtp(payload: {
    otpSentVia: string;
    email?: string;
    phoneNumber: string;
    otp: string;
  }): Observable<any> {
    return this.http.post(this.urlRef.getValidateOtpUrl(), payload);
  }

  getUserData(): any {
    return this.userData;
  }

  setUserData(data: any): void {
    this.userData = data;
  }

  getTokenExpiry(): number {
    if (!this.keycloak.tokenParsed || !this.keycloak.tokenParsed.exp) {
      return 0;
    }
    return this.keycloak.tokenParsed.exp;
  }

  onCancelPendingHttpRequests(): Observable<any> {
    return new Observable();
  }

  /**
   * Clear additional localStorage keys on logout
   * This method can be called by other services to clean up their specific data
   */
  clearAdditionalStorageKeys(keys: string[]): void {
    keys.forEach((key) => {
      localStorage.removeItem(key);
      sessionStorage.removeItem(key);
    });
    console.log('Additional storage keys cleared:', keys);
  }

  // Optional but recommended for Keycloak
  private cleanupOldKeycloakCallbacks(): void {
    try {
      Object.keys(localStorage).forEach((key) => {
        // if (key.startsWith('kc-callback-')) {
        //   localStorage.removeItem(key);
        // }
      });
    } catch (e) {
      console.warn('Error during Keycloak callback cleanup:', e);
    }
  }
}
